import { ClientGroupAccessibilityEnum } from '@DigitaleDoerfer/digitale-doerfer-api';

const membersDE = {
  members_zero: 'Keine Mitglieder',
  members_one: '1 Mitglied',
  members_other: '{{count}} Mitglieder'
};

const membersEN = {
  members_zero: 'no members',
  members_one: '1 member',
  members_other: '{{count}} members'
};

export const groupsGermanLocalization = {
  routes: {
    'group-detail': membersDE
  },
  components: {
    groupListItem: {
      ...membersDE,
      alerts: {
        pending: {
          title: 'Anfrage in Bearbeitung',
          content:
            'Du hast bereits eine Beitrittsanfrage an diese Gruppe gesendet. Ein Mitglied der Gruppe wird sie bald bearbeiten.',
          confirm: 'Alles klar'
        },
        rejected: {
          title: 'Anfrage abgelehnt',
          content:
            'Leider wurde deine Beitrittsanfrage abgelehnt. Du kannst in 5 Tagen erneut eine Anfrage stellen.',
          confirm: 'Alles klar'
        }
      }
    },
    groupMemberItem: {
      you: 'Du',
      admin: 'Admin'
    },
    groupMemberMenu: {
      writePrivate: 'Privat schreiben',
      showProfile: 'Profil zeigen',
      addAdminStatus: 'Zu Gruppenadmin ernennen',
      removeAdminStatus: 'Adminstatus entfernen',
      removeFromGroup: 'Aus Gruppe entfernen',
      alerts: {
        remove: {
          title: '{{name}} aus Gruppe entfernen?',
          content:
            '{{name}} wird aus der Gruppe entfernt und kann nicht mehr auf die Gruppe und deren Inhalte zugreifen.',
          confirm: 'Entfernen',
          cancel: 'Abbrechen'
        }
      }
    }
  },
  screens: {
    groups: {
      title: 'Funkzentrale',
      my: {
        title: 'Meine Gruppen',
        empty:
          'Tritt einer Gruppe bei oder erstell Gruppen für deine Herzensthemen.'
      },
      area: {
        title: 'Gruppen im Umkreis',
        empty: 'Es gibt noch keine Gruppen im Umkreis.'
      },
      create: 'Gruppe erstellen'
    },
    group: {
      join: 'Gruppe beitreten',
      menu: {
        name: 'Optionen',
        joinGroup: 'Gruppe beitreten',
        members: 'Mitglieder',
        edit: 'Gruppe bearbeiten',
        leaveGroup: 'Gruppe verlassen',
        reportViolation: 'Verstoß melden'
      },
      requests_one: '1 offene Beitrittsanfrage',
      requests_other: '{{count}} offene Beitrittsanfragen',
      alerts: {
        leave: {
          title: 'Gruppe verlassen?',
          content: 'Bist du sicher, dass du die Gruppe verlassen möchtest?',
          confirm: 'Verlassen',
          cancel: 'Abbrechen'
        }
      },
      ...membersDE
    },
    'group-editor': {
      header: {
        create: 'Gruppe erstellen',
        edit: 'Gruppe bearbeiten'
      },
      name: 'Gruppenname',
      description: 'Beschreibung (optional)',
      descriptionHint: 'Beschreibe, um was es bei der Gruppe geht.',
      access: {
        title: 'Zutritt',
        [ClientGroupAccessibilityEnum.Public]: 'Öffentlich',
        [ClientGroupAccessibilityEnum.ApprovalRequired]: 'Nur auf Anfrage'
      },
      'geo-area': {
        title: 'Ort der Gruppe',
        description: 'Wähle den Hauptort der Gruppe',
        searchPlaceholder: 'Suche nach PLZ oder Name',
        map: 'Wähle den Umkreis der Gruppe'
      },
      button: {
        next: 'Weiter',
        create: 'Gruppe erstellen',
        edit: 'Gruppe aktualisieren',
        delete: 'Gruppe löschen'
      },
      alerts: {
        delete: {
          title: 'Gruppe löschen?',
          content: 'Sämtliche Inhalte werden unwiderruflich gelöscht.',
          confirm: 'Löschen',
          cancel: 'Abbrechen'
        }
      }
    },
    members: membersDE,
    description: {
      join: 'Beitreten',
      pendingAlert: {
        title: 'Anfrage in Bearbeitung',
        body: 'Du hast bereits eine Beitrittsanfrage an diese Gruppe gesendet. Ein Mitglied der Gruppe wird sie bald bearbeiten.',
        confirm: 'Alles klar'
      },
      reportViolation: 'Verstoß melden'
    },
    request: {
      title: 'Beitritt anfragen?',
      description:
        'Die Gruppe ist nur für eingeladene Funker zugänglich. Bei Interesse kannst du eine Anfrage an die Gruppenadmins stellen.',
      joinGroup: 'Beitrittsanfrage stellen',
      message: 'Nachricht (optional)',
      messageHint: 'Stell dich kurz vor...',
      ...membersDE
    },
    requests: {
      accept: 'Annehmen',
      deny: 'Ablehnen'
    }
  }
};

// Guarantees that both translations have the same structure
type Locales = typeof groupsGermanLocalization;

export const groupsEnglishLocalization: Locales = {
  routes: {
    'group-detail': membersEN
  },
  components: {
    groupListItem: {
      ...membersEN,
      alerts: {
        pending: {
          title: 'Request in Progress',
          content:
            'You have already sent a join request to this group. A member of the group will process it soon.',
          confirm: 'All right'
        },
        rejected: {
          title: 'Request Rejected',
          content:
            'Unfortunately, your join request has been rejected. You can submit a request again in 5 days.',
          confirm: 'All right'
        }
      }
    },
    groupMemberItem: {
      you: 'You',
      admin: 'Admin'
    },
    groupMemberMenu: {
      writePrivate: 'Write private',
      showProfile: 'Show profile',
      addAdminStatus: 'Promote to group admin',
      removeAdminStatus: 'Remove admin status',
      removeFromGroup: 'Remove from group',
      alerts: {
        remove: {
          title: 'Remove {{name}} from group?',
          content:
            '{{name}} will be removed from the group and can no longer access the group or its content.',
          confirm: 'Remove',
          cancel: 'Cancel'
        }
      }
    }
  },
  screens: {
    groups: {
      title: 'Local Hub',
      my: {
        title: 'My Groups',
        empty: 'Join a group or create groups for your favorite topics.'
      },
      area: {
        title: 'Groups in the area',
        empty: 'There are no groups in the area yet.'
      },
      create: 'Create Group'
    },
    group: {
      join: 'Join the group',
      menu: {
        name: 'Options',
        members: 'Members',
        edit: 'Edit group',
        joinGroup: 'Join group',
        leaveGroup: 'Leave group',
        reportViolation: 'Report violation'
      },
      requests_one: '1 open join request',
      requests_other: '{{count}} open join requests',
      alerts: {
        leave: {
          title: 'Leave group?',
          content: 'Are you sure you want to leave the group?',
          confirm: 'Leave',
          cancel: 'Cancel'
        }
      },
      ...membersEN
    },
    'group-editor': {
      header: {
        create: 'Create Group',
        edit: 'Edit Group'
      },
      name: 'Group Name',
      description: 'Description (optional)',
      descriptionHint: 'Describe what the group is about.',
      access: {
        title: 'Access',
        [ClientGroupAccessibilityEnum.Public]: 'Public',
        [ClientGroupAccessibilityEnum.ApprovalRequired]: 'By Request Only'
      },
      'geo-area': {
        title: 'Group Location',
        description: 'Select the main location of the group',
        searchPlaceholder: 'Search for postal code or name',
        map: 'Choose the radius of the group'
      },
      button: {
        next: 'Next',
        create: 'Create group',
        edit: 'Update group',
        delete: 'Delete group'
      },
      alerts: {
        delete: {
          title: 'Delete group?',
          content: 'All content will be irreversibly deleted.',
          confirm: 'Delete',
          cancel: 'Cancel'
        }
      }
    },
    members: membersEN,
    description: {
      join: 'Join',
      pendingAlert: {
        title: 'Request pending',
        body: 'You have already sent a request to join this group. A member of the group will process it shortly.',
        confirm: 'Okay'
      },
      reportViolation: 'Report violation'
    },
    request: {
      title: 'Request to join?',
      description:
        'The group is only accessible to invited members. If you are interested, you can send a request to the group admins.',
      joinGroup: 'Submit join request',
      message: 'Message (optional)',
      messageHint: 'Introduce yourself briefly...',
      ...membersEN
    },
    requests: {
      accept: 'Accept',
      deny: 'Deny'
    }
  }
};
